import type { ApprovalLineItem } from '@safetyhub/report-composer';
import { isApprovalSlotUnresolved } from '@safetyhub/shared';

/**
 * "결재자 신원이 유실된 단계" 판별.
 *
 * `sourceType='member'`(= 결재자 단계)인데 그 결재자를 식별할 값(`memberId` — 구 `approverProfileId`
 * 축은 2026-09-16 E4에서 삭제)이 없는 행을 가리킨다. 2026-07-24 이전 `setReportApprovalLines`가 타사(현장 연결 업체) 멤버
 * 단계를 조용히 스킵해 `approval_line_definition`에서 결재자 행이 소실됐고, 그 행을 서명행
 * (`approval_signatures`)의 이름·직위로 **표시만** 복원한 백필분이 여기 해당한다
 * (마이그레이션 `20260728100000_backfill_composed_approval_line_definition`).
 *
 * 이 행은 편집 모달에서 이름이 정상으로 보이지만(서명행에서 복원된 값), 저장 변환은
 * `memberId`가 없으면 author로 떨어뜨린다. 그대로 두면
 *  - 보고서 저장 payload: `sourceType`이 'author'로 강등돼 **복원한 결재자 단계가 다시 소실**되고
 *  - 현장 기본 저장: `assignmentChain`이 `[{type:'author'}]`가 돼 **현장 메타까지 오염**된다.
 * 2026-07-24 사고와 같은 "조용한 강등"이므로, 값을 지어내지 말고 저장을 막고 재선택을 요구한다.
 */
export function isIdentityLostApprovalStep(line: ApprovalLineItem): boolean {
  return line.sourceType === 'member' && !line.memberId;
}

/** 신원 유실 단계가 있으면 그 index, 없으면 -1. */
export function findIdentityLostApprovalStep(lines: ApprovalLineItem[]): number {
  return lines.findIndex(isIdentityLostApprovalStep);
}

/** 신원 유실 단계 저장 차단 안내 문구. `stepLabel`은 사용자가 그 칸을 찾을 수 있는 단계 라벨. */
export function identityLostApprovalStepMessage(stepLabel: string): string {
  return `'${stepLabel}' 단계의 결재자 정보가 유실되어 저장할 수 없습니다. 해당 단계의 결재자를 다시 선택하거나 단계를 삭제해 주세요.`;
}

/** 편집 목록에서 신원 유실 단계임을 이름 옆에 알리는 접미 문구. */
export const IDENTITY_LOST_NAME_SUFFIX = ' (결재자 재선택 필요)';

/**
 * "담당자 미지정 칸" 판별 — 칸은 있으나 담당자가 없다(= 오프라인 수기 결재 칸).
 *
 * 위 신원 유실과 **다른 상태**다. 신원 유실은 결재자 단계인데 식별자가 사라진 사고이고,
 * 미지정은 작성자가 의도적으로 비운 정상 상태다(결정 2026-08-13).
 */
// 마커 하나만 읽으므로 최소 형상으로 받는다 — 제출 완료형 판정(approvalCompletion)도 같은 판정을 쓴다.
export function isUnassignedApprovalStep(line: Pick<ApprovalLineItem, '_assigneeUnset'>): boolean {
  return line._assigneeUnset === true;
}

/**
 * 담당자 지정이 **앞에서부터 연속(prefix)** 인지 검사하고, 위반한 첫 칸의 index를 반환한다
 * (없으면 -1). 서버 `findApprovalSlotPrefixViolation`과 같은 규칙을 화면에서 미리 적용한다.
 *
 * 근거(결정 2026-08-13 §2): 순차 결재에서 미지정(오프라인) 칸의 완료 신호는 시스템 밖에서
 * 발생하므로, 중간에 끼면 다음 단계를 깨울 트리거가 사라진다 — 결재가 영영 멈추거나,
 * 무시하고 넘기면 "검토 없이 승인"이 된다. 그래서 지정 구간은 항상 앞쪽 연속이어야 한다.
 *
 * 이 검사가 없으면 사용자는 **저장을 눌러야** 서버 400
 * (`APPROVAL_LINE_ASSIGNEE_ORDER_VIOLATION`)으로 알 수 있다.
 */
export function findApprovalPrefixViolation(lines: ApprovalLineItem[]): number {
  const firstUnassigned = lines.findIndex(isUnassignedApprovalStep);
  if (firstUnassigned < 0) return -1;
  const violationIndex = lines.findIndex(
    (line, index) => index > firstUnassigned && !isUnassignedApprovalStep(line),
  );
  return violationIndex;
}

/**
 * 안내 문구에 쓰는 단계 라벨 — 커스텀 라벨이 없으면 순번(`N단계`).
 *
 * 편집 모달(`useApprovalLineEdit.getStepLabel`)과 위임 모달이 **같은 규칙**을 써야 한다.
 * 두 경로가 각자 라벨을 만들면 같은 위반에 다른 이름이 붙어 사용자가 다른 칸을 찾는다.
 */
export function approvalStepLabel(lines: ApprovalLineItem[], index: number): string {
  return lines[index]?.label || `${index + 1}단계`;
}

/**
 * "미해소 칸" 판별 — 담당자 소스는 지정됐는데(체인 있음) 그 사람을 찾지 못한 칸(S73).
 *
 * 예: `[{role:'SAFETY_MANAGER'}]`인데 그 현장에 안전관리자 배정이 없다. 화면에는 이름 자리에
 * 서버 폴백 `(담당자)`가 뜨므로 **지정된 칸처럼 보인다** — 그래서 사람 눈으로는 안 걸린다.
 *
 * ⚠️ {@link isUnassignedApprovalStep}(미지정 칸)과 **다른 상태**다. 미지정은 작성자가 의도적으로
 * 비운 정상 상태(오프라인 수기 결재)라 제출이 통과해야 하고, 미해소는 결재를 받을 사람이 없어
 * 제출이 막혀야 한다. 판정 술어는 서버와 **같은 함수**(`@safetyhub/shared`의
 * `isApprovalSlotUnresolved`)를 쓴다 — 두 벌이면 한쪽만 고쳐진다.
 */
export function isUnresolvedApprovalStep(line: ApprovalLineItem): boolean {
  return isApprovalSlotUnresolved({
    memberId: line.memberId ?? null,
    assignmentChain: line._assignmentChain,
    // author 칸은 작성자 서명이 채우는 칸이라 담당자가 비어도 미해소가 아니다 — 이 축을
    // 빠뜨리면 작성자 단계가 있는 보고서가 전부 제출 불가가 된다(공용 술어 주석 참조).
    sourceType: line.sourceType,
  });
}

/** 미해소 칸이 있으면 그 index, 없으면 -1. */
export function findUnresolvedApprovalStep(lines: ApprovalLineItem[]): number {
  return lines.findIndex(isUnresolvedApprovalStep);
}

/**
 * 이 칸의 담당자를 **위임이 정하는가** — 체인 머리가 `delegatedTo`인가 (2026-09-09).
 *
 * 머리만 본다. 뒤쪽 폴백(`author` 등)은 *위임이 없을 때* 쓰는 값이라, 위임 흐름 안에서는
 * 그 폴백을 사람 이름으로 보여 주면 거짓이 된다 — 저장하면 서버가 위임 담당자로 해소하므로
 * **미리보기만 다른 사람을 가리키게** 된다(2026-09-09 dev: 김대표를 골랐는데 1행은 임경업).
 *
 * ⚠️ 이 술어만으로 화면을 바꾸면 안 된다. 위임하지 않은 보고서의 같은 칸은 폴백이 **최종
 * 답**이라(작성자가 실제로 결재한다) 「위임 담당자」라 쓰면 그쪽이 거짓이 된다. 그래서
 * 호출부가 «위임 흐름인가»를 함께 준다(EditModal의 `delegationSlot`).
 */
export function isDelegationOwnedStep(line: ApprovalLineItem): boolean {
  return line._assignmentChain?.[0]?.type === 'delegatedTo';
}

/*
 * ⚠️ **제출 차단 안내 문구(`unresolvedApprovalStepMessage`)는 2026-08-27에 제거됐다** —
 * 미해소 칸은 더 이상 제출을 막지 않고 그 칸에서 **대기**한다(결정
 * `2026-08-27-unresolved-approval-slot-park-and-resume.md`, S73 대체).
 * 그 자리의 안내는 상세 배너(`ParkedApprovalBanner` + `manifest/parkedApprovalBannerPolicy`)가
 * 맡는다 — 제출 전에는 예고로, 제출 후에는 대기 상태로 문구가 갈린다.
 */

/**
 * 편집 목록에서 미해소 칸임을 이름 옆에 알리는 접미 문구 (I-1, S74).
 *
 * `IDENTITY_LOST_NAME_SUFFIX`와 같은 자리·같은 방식이다 — 이름 자체(`getLineName`)는 건드리지
 * 않는다. 그 값은 결재란 미리보기 칸과 삭제 확인 문구도 함께 쓰고, 미리보기는 **실제 결재란
 * 모습**이어야 하기 때문이다.
 *
 * 왜 필요한가: 미해소 칸은 이름 자리에 서버 폴백 `(담당자)`가 떠 **사람 이름처럼 보인다**
 * (2026-08-26 dev 실측 캡처). 사용자는 그 칸이 정상이라고 읽고 제출했다가 막힌다.
 */
export const UNRESOLVED_NAME_SUFFIX = ' (담당자 지정 필요)';

/**
 * 미해소 칸의 편집 목록 경고 문구 (I-1, S74).
 *
 * ⚠️ **연락처 없음 경고(F-F)를 이 칸에 쓰면 거짓말이 된다.** 그쪽은 *"인앱 알림으로만 전달되며,
 * 담당자가 로그인하지 않으면 결재 요청을 보지 못합니다"* 라고 말하는데, 미해소 칸은 **받을 사람
 * 자체가 없어서** 인앱도 가지 않는다. 두 상태는 증상(연락처 종류 0)이 같아 한 문구로 묶이기
 * 쉬운데, 원인과 조치가 전혀 다르다 — F-F는 그 사람의 연락처를 채우는 문제이고, 이건 그 자리에
 * 사람을 앉히는 문제다. 2026-08-26 실측 캡처에서 이 칸이 F-F 문구를 달고 정상처럼 보였다.
 *
 * 2026-08-27: *"이대로는 제출할 수 없으니"* 를 **대기 취지로 고쳤다** — 제출은 이제 통과하고
 * 그 칸에서 멈춘다(park-and-resume). 옛 문구를 두면 화면이 거짓을 말한다.
 */
export const UNRESOLVED_ROW_WARNING =
  '이 칸의 담당자를 찾을 수 없어 결재를 받을 사람이 없습니다. 이대로 제출하면 결재가 이 칸에서 대기하니, 담당자를 직접 지정하거나 현장에 해당 역할의 담당자를 배정해 주세요.';

/**
 * PENDING_APPROVAL **대기 칸 지정 모드**(park-and-resume C2)의 대기 칸 경고 문구.
 *
 * `UNRESOLVED_ROW_WARNING`과 갈라 둔다 — 그쪽은 *"이대로 제출하면"* 이라는 제출 전 예고인데,
 * 이 모드의 문서는 **이미 제출돼 이 칸에서 멈춰 있다.** 같은 문구를 쓰면 화면이 거짓을 말한다
 * (배너 정책 파일이 전/후 문구를 가른 것과 같은 이유).
 */
export const PARKED_FILL_ROW_WARNING =
  '이 칸의 담당자가 없어 결재가 여기서 대기 중입니다. 담당자를 지정하면 결재가 재개됩니다.';

/** prefix 위반 저장 차단 안내 문구. `stepLabel`은 사용자가 그 칸을 찾을 수 있는 단계 라벨. */
export function approvalPrefixViolationMessage(stepLabel: string): string {
  return `앞 칸의 담당자가 지정되지 않아 '${stepLabel}' 단계를 저장할 수 없습니다. 담당자는 앞에서부터 차례로 지정해야 합니다 — 앞 칸에 담당자를 지정하거나 이 칸의 담당자를 해제해 주세요.`;
}
